import { useState } from "react";
import { Flag, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  reportChildPhoto,
  REPORT_REASONS,
} from "@/lib/photoModeration";
import useFocusTrap from "@/lib/hooks/useFocusTrap";

/**
 * Bottom-sheet (mobile) / centered dialog (desktop) for the parent to
 * flag a photo. Matches the `teacher-photos-caption-sheet` pattern so
 * the UX feels consistent across the photo flows.
 *
 * Flow:
 *   1. Parent picks a canned reason (radio) + optional free-form note.
 *   2. Submit hits rpc_report_child_photo; on success we toast and fire
 *      onSuccess(photoId). RLS hides flagged photos from the parent
 *      immediately, so the caller should drop it from local state.
 */
export default function PhotoReportSheet({ photoId, onClose, onSuccess }) {
  const [reasonKey, setReasonKey] = useState(REPORT_REASONS[0].key);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // Focus trap keeps Tab/Shift+Tab inside the sheet and closes on Escape.
  // Restores focus to the triggering element when the sheet unmounts.
  const trapRef = useFocusTrap({ active: true, onEscape: () => !saving && onClose?.() });

  // Clicks inside the sheet must not bubble to the lightbox backdrop.
  const stop = (e) => e.stopPropagation();

  async function submit() {
    const reasonLabel = REPORT_REASONS.find((r) => r.key === reasonKey)?.label || "";
    // RPC requires a non-empty string. If "Something else" + blank note,
    // fall back to the label; otherwise include the note for context.
    const composed = note.trim()
      ? `${reasonLabel}: ${note.trim()}`
      : reasonLabel;
    setSaving(true);
    try {
      await reportChildPhoto(photoId, composed);
      toast.success("Photo reported to your admin for review");
      onSuccess?.(photoId);
    } catch (err) {
      toast.error(err?.message || "Could not send report");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="photo-report-sheet"
      className="fixed inset-0 z-[70] flex items-end bg-black/50 sm:items-center sm:justify-center"
      onClick={onClose}
    >
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="photo-report-title"
        className="w-full max-w-md rounded-t-3xl bg-white p-5 sm:rounded-2xl"
        onClick={stop}
      >
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-[#C6851E]/10 text-[#C6851E]">
            <Flag size={18} weight="duotone" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="photo-report-title" className="font-heading text-lg font-semibold text-neutral-900">
              Report this photo
            </h2>
            <p className="mt-0.5 text-xs leading-relaxed text-neutral-500">
              Your admin will review it shortly. The photo is hidden from
              your feed while it's in review.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-neutral-100"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <fieldset className="space-y-1.5" data-testid="photo-report-reasons">
          <legend className="mb-1 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
            Why are you reporting?
          </legend>
          {REPORT_REASONS.map((r) => (
            <label
              key={r.key}
              data-testid={`photo-report-reason-${r.key}`}
              data-selected={reasonKey === r.key}
              className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-3 py-2.5 text-sm transition ${
                reasonKey === r.key
                  ? "border-primary/50 bg-primary/5 text-neutral-900"
                  : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              <input
                type="radio"
                name="report-reason"
                value={r.key}
                checked={reasonKey === r.key}
                onChange={() => setReasonKey(r.key)}
                className="h-4 w-4 accent-primary"
              />
              <span className="flex-1">{r.label}</span>
            </label>
          ))}
        </fieldset>

        <label className="mt-3 block">
          <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
            Additional context (optional)
          </span>
          <textarea
            data-testid="photo-report-note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 240))}
            rows={2}
            placeholder="Anything your admin should know?"
            className="mt-1 w-full rounded-2xl border border-neutral-200 p-3 text-sm focus:border-primary focus:outline-none"
          />
          <span className="mt-0.5 block text-right text-[10px] text-neutral-400">
            {note.length}/240
          </span>
        </label>

        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            data-testid="photo-report-cancel"
            disabled={saving}
            onClick={onClose}
            className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="photo-report-submit"
            disabled={saving}
            onClick={submit}
            className="rounded-full bg-[#C6851E] px-4 py-2 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-60"
          >
            {saving ? "Reporting…" : "Send report"}
          </button>
        </div>
      </div>
    </div>
  );
}
